import { useConfiguratorProductCode } from "~/features/configurator/common/useConfiguratorType";
import { getConfiguratorFamilyFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import {
  getXmlConfiguratorTranslationConfig,
  XML_TRANSLATION_SHEETS,
} from "~/domains/configurator/translations/configuratorTranslationConfig";
import { useTranslation } from "react-i18next";
import { type TFunction, type WithT } from "i18next";
import { useSelector } from "react-redux";
import type { RootState } from "~/app/store";

type ReturnType = {
  t: TFunction;
  tPrefix: string;
  renderStepName: (index: number) => string;
};

const UNDEFINED_RETURN_TYPE: UndefinedReturnType = {
  t: undefined,
  tPrefix: undefined,
  renderStepName: undefined,
};
type UndefinedReturnType = Record<keyof ReturnType, undefined>;
export function useXmlConfiguratorTranslations(): ReturnType | UndefinedReturnType {
  const productCode = useConfiguratorProductCode();
  return useForcedConfiguratorTranslations(productCode);
}

export function useForcedConfiguratorTranslations(productCode: string): ReturnType | UndefinedReturnType {
  const config = getXmlConfiguratorTranslationConfig(productCode);
  const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);
  const { t: originalT } = useTranslation(config?.sheetName);
  const steps = useSelector((state: RootState) => state.linearConfigurator.steps);
  if (!config) {
    return UNDEFINED_RETURN_TYPE;
  }
  const t = ((key: string, options?: object) =>
    originalT(key, { context: configuratorFamily, ...options })) as WithT["t"];
  return {
    t,
    tPrefix: config?.tPrefix,
    renderStepName: (index: number) => {
      return t(steps[index].name);
    },
  };
}

export { getXmlConfiguratorTranslationConfig, XML_TRANSLATION_SHEETS };
